import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { PageActionsComponent, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { MODAL_TOKEN, ModalProvider, NAVIGATION_TOKEN, NavigationProvider, PresentModalComponent } from '@core';
import { getFirebaseAuthErrorMessage } from '@auth';
import { Observable, take } from 'rxjs';
import { ChangePasswordCommandHandler } from '../../../../../auth/src/lib/application/handlers';

const passwordsMatchValidator = (group: AbstractControl): ValidationErrors | null => {
  const newPassword: string = group.get('newPassword')?.value;
  const confirmPassword: string = group.get('confirmPassword')?.value;
  return newPassword && confirmPassword && newPassword !== confirmPassword ? { passwordsMismatch: true } : null;
};

@Component({
  selector: 'lib-change-password',
  templateUrl: './change-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgClass, IonicModule, ReactiveFormsModule, PageLayoutComponent, PageActionsComponent, TitileNavbarComponent]
})
export class ChangePasswordComponent {
  private readonly changePasswordCommandHandler: ChangePasswordCommandHandler = inject(ChangePasswordCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);

  readonly minLength: number = 6;

  readonly passwordForm: FormGroup = new FormGroup(
    {
      currentPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [Validators.required, Validators.minLength(this.minLength)]),
      confirmPassword: new FormControl('', [Validators.required])
    },
    { validators: passwordsMatchValidator }
  );

  get isNewPasswordTooShort(): boolean {
    const control: AbstractControl | null = this.passwordForm.get('newPassword');
    return !!control?.value && control.value.length < this.minLength;
  }

  get isConfirmMismatch(): boolean {
    const confirm: AbstractControl | null = this.passwordForm.get('confirmPassword');
    return !!confirm?.value && this.passwordForm.hasError('passwordsMismatch');
  }

  changePassword(): void {
    if (this.passwordForm.invalid) return;

    this.changePasswordCommandHandler
      .changePassword(this.passwordForm.get('currentPassword')?.value, this.passwordForm.get('newPassword')?.value)
      .pipe(take(1))
      .subscribe({
        next: () =>
          this.showSuccessMessage()
            .pipe(take(1))
            .subscribe(() => this.navigationProvider.back()),
        error: (error: unknown) => this.showErrorMessage(error)
      });
  }

  private showSuccessMessage(): Observable<void> {
    return this.modalProvider.showModal$({
      component: PresentModalComponent,
      componentProps: {
        header: 'Hasło zmienione',
        message: 'Twoje hasło zostało pomyślnie zmienione.',
        btnTxt: 'Zamknij'
      },
      cssClass: 'present-modal'
    });
  }

  private showErrorMessage(error: unknown): void {
    const { header, message } = getFirebaseAuthErrorMessage(error, 'Zmień hasło');
    this.modalProvider.showModal$({
      component: PresentModalComponent,
      componentProps: { header, message, btnTxt: 'Zamknij' },
      cssClass: 'present-modal'
    });
  }
}
